import { FormContext, FormProps } from '../contexts/Form.context'
import { ReactElement, ReactNode, useState } from 'react'
import { FormRenderer, UseFormReturn } from '../contexts/Form.context'
import Modal, { ModalProps } from '../components/Modal'

export const FormProvider = ({ children }: { children: ReactNode }) => {
  const [formElement, setFormElement] = useState<ReactElement | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const renderForm: FormRenderer = <T, K>(
    FormComponent: React.FC<FormProps<T, K>>,
    submitHandler?: (data: T) => Promise<{ ok: boolean }>,
    initialValue?: Partial<T>,
    extras?: K,
    modalProps?: Omit<ModalProps, 'children'>
  ) => {
    return new Promise<UseFormReturn<T>>((resolve) => {
      const handleSubmit = async (data: T) => {
        setIsLoading(true)
        const { ok } = (await submitHandler?.(data)) || { ok: true }
        setIsLoading(false)
        if (!ok) {
          return handleError()
        }
        resolve({ status: 'success', data })
        setFormElement(null)
      }

      const handleAbort = () => {
        resolve({ status: 'aborted' })
        setFormElement(null)
      }

      const handleError = () => {
        resolve({ status: 'error' })
      }

      const element = (
        <Modal {...modalProps} onClose={handleAbort}>
          <FormComponent
            initialValue={initialValue}
            onSubmit={handleSubmit}
            onAbort={handleAbort}
            onError={handleError}
            extras={extras || ({} as K)}
          />
        </Modal>
      )

      setFormElement(element)
    })
  }

  return (
    <FormContext.Provider value={{ formRenderer: renderForm, isLoading }}>
      {children}
      {formElement}
    </FormContext.Provider>
  )
}
